import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Grid, MenuItem, TextField } from "@mui/material";
import { Link } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

const selectFieldProps = {
  InputLabelProps: { shrink: true },
  sx: {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      alignItems: "center",
    },
  },
};

function AgriBoost() {
  const [topicFilterName] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("topic_name") || "";
  });
  const [items, setItems] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      search: "",
      subject_id: params.get("subject_id") || "",
      topic_id: params.get("topic_id") || "",
      status: "",
      is_active: "",
    };
  });
  const [notification, setNotification] = useState({ open: false, color: "success", message: "" });

  const showMessage = (message, color = "success") => {
    setNotification({ open: true, color, message });
    setTimeout(() => setNotification({ open: false, color, message: "" }), 2500);
  };

  const loadSubjects = async () => {
    try {
      const resp = await axios.get(Constants.getUrls.getAgriBoostMetaSubjects);
      setAllSubjects(resp?.data?.data || []);
    } catch (error) {
      showMessage("Unable to load subjects.", "error");
    }
  };

  const loadTopics = async (subjectId = filters.subject_id) => {
    try {
      const url = subjectId
        ? `${Constants.getUrls.getAgriBoostMetaTopics}?subject_id=${subjectId}`
        : Constants.getUrls.getAgriBoostMetaTopics;
      const resp = await axios.get(url);
      setAllTopics(resp?.data?.data || []);
    } catch (error) {
      showMessage("Unable to load topics.", "error");
    }
  };

  const loadItems = async (page = pageNo, currentLimit = limit, currentFilters = filters) => {
    if (init() !== "success") return;

    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(currentLimit),
      });

      if (currentFilters.search) params.append("search", currentFilters.search);
      if (currentFilters.subject_id) params.append("subject_id", currentFilters.subject_id);
      if (currentFilters.topic_id) params.append("topic_id", currentFilters.topic_id);
      if (currentFilters.status) params.append("status", currentFilters.status);
      if (currentFilters.is_active !== "") params.append("is_active", currentFilters.is_active);

      const resp = await axios.get(`${Constants.getUrls.getAllAgriBoosts}?${params.toString()}`);
      setItems(resp?.data?.data?.docs || []);
      setPagination(resp?.data?.data || {});
      setPageNo(resp?.data?.data?.page || page);
      setLimit(resp?.data?.data?.limit || currentLimit);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to load AgriBoost.", "error");
    }
  };

  useEffect(() => {
    loadSubjects();
    loadTopics(filters.subject_id);
    loadItems(1, limit, filters);
  }, []);

  const handleDelete = async (id) => {
    const confirmed = window.confirm("Are you sure you want to delete this AgriBoost?");
    if (!confirmed) return;

    try {
      const resp = await axios.delete(`${Constants.deleteUrls.deleteAgriBoost}${id}`);
      showMessage(resp?.data?.message || "AgriBoost deleted successfully.");
      loadItems(pageNo, limit, filters);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to delete AgriBoost.", "error");
    }
  };

  const handleFilterChange = (name, value) => {
    const nextFilters = {
      ...filters,
      [name]: value,
    };

    if (name === "subject_id") {
      nextFilters.topic_id = "";
      loadTopics(value);
    }

    setFilters(nextFilters);
  };

  const applyFilters = () => {
    loadItems(1, limit, filters);
  };

  const rows = items.map((item, index) => ({
    serial: (pageNo - 1) * limit + index + 1,
    order: item?.display_order ?? 0,
    question: (
      <MDBox>
        <MDTypography variant="button" fontWeight="medium" display="block">
          {item?.question_english || "-"}
        </MDTypography>
        <MDTypography variant="caption" color="secondary" display="block">
          {item?.question_hindi || "-"}
        </MDTypography>
      </MDBox>
    ),
    subject: item?.subject_id?.subject_name || "-",
    topic: item?.topic_id?.topic_name || "-",
    reveal: `${item?.reveal_time || 0} sec`,
    status: item?.status || "-",
    active: item?.is_active ? "Active" : "Inactive",
    action: (
      <MDBox display="flex" gap={1}>
        <Link to={`/update-agriboost/${item._id}`}>
          <MDButton variant="outlined" size="small" color="info">
            Update
          </MDButton>
        </Link>
        <MDButton variant="outlined" size="small" color="error" onClick={() => handleDelete(item._id)}>
          Delete
        </MDButton>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              AgriBoost
              {topicFilterName ? ` • ${topicFilterName}` : ""}
            </MDTypography>
          </MDBox>

          <MDBox px={2} py={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Subject"
                  value={filters.subject_id}
                  onChange={(e) => handleFilterChange("subject_id", e.target.value)}
                  {...selectFieldProps}
                >
                  <MenuItem value="">All Subjects</MenuItem>
                  {allSubjects.map((item) => (
                    <MenuItem key={item._id} value={item._id}>
                      {item.subject_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Topic"
                  value={filters.topic_id}
                  onChange={(e) => handleFilterChange("topic_id", e.target.value)}
                  {...selectFieldProps}
                >
                  <MenuItem value="">All Topics</MenuItem>
                  {allTopics.map((item) => (
                    <MenuItem key={item._id} value={item._id}>
                      {item.topic_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  {...selectFieldProps}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={1}>
                <TextField
                  select
                  fullWidth
                  label="Active"
                  value={filters.is_active}
                  onChange={(e) => handleFilterChange("is_active", e.target.value)}
                  {...selectFieldProps}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <MDButton variant="contained" color="info" onClick={applyFilters} fullWidth>
                  Apply
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>

          <Grid item sm={4} px={2} pb={2} align="right">
            <MDBox display="flex" justifyContent="flex-end" gap={1}>
              <Link to="/add-bulk-agriboost">
                <MDButton variant="contained" size="small" color="info">
                  Bulk Import
                </MDButton>
              </Link>
              <Link to="/add-agriboost">
                <MDButton variant="contained" size="small" color="primary">
                  Create AgriBoost
                </MDButton>
              </Link>
            </MDBox>
          </Grid>

          <MDBox>
            <DataTable
              table={{
                columns: [
                  { Header: "serial", accessor: "serial", width: "6%" },
                  { Header: "Display Order", accessor: "order" },
                  { Header: "Question", accessor: "question" },
                  { Header: "Subject", accessor: "subject" },
                  { Header: "Topic", accessor: "topic" },
                  { Header: "Reveal Time", accessor: "reveal" },
                  { Header: "Status", accessor: "status" },
                  { Header: "Active", accessor: "active" },
                  { Header: "Action", accessor: "action" },
                ],
                rows,
              }}
              entriesPerPage={{ defaultValue: limit, entries: [10, 25, 50, 100] }}
              page_no={pagination?.page}
              totalPages={pagination?.totalPages}
              hasNextPage={pagination?.hasNextPage}
              hasPrevPage={pagination?.hasPrevPage}
              isnextPage={pagination?.nextPage}
              prevPage={pagination?.prevPage}
              totalDocs={pagination?.totalDocs}
              onPageChange={(page) => loadItems(page, limit, filters)}
              onEntriesPerPageChange={(newLimit) => loadItems(1, newLimit, filters)}
            />
          </MDBox>
          <MDSnackbar
            color={notification.color}
            icon="notifications"
            title="AgriQuiz"
            content={notification.message}
            dateTime="Just now"
            open={notification.open}
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default AgriBoost;
